/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2020 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers.  The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */
import Spinner from "@eisinc/App/Components/Spinner/Spinner";
import Button, { ButtonPresentationProps } from "@eisinc/MobiusOverrides/Button";
import CheckboxGroup, { CheckboxGroupComponentProps } from "@eisinc/MobiusOverrides/CheckboxGroup";
import Modal, { ModalPresentationProps } from "@eisinc/MobiusOverrides/Modal";
import TextField, { TextFieldProps } from "@eisinc/MobiusOverrides/TextField";
import { screen } from "@eisinc/XDL/Helpers";
import { color } from "@eisinc/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import signIn from "@insite/client-framework/Store/Context/Handlers/SignIn";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import SignInResetPasswordForm from "@insite/content-library/Widgets/SignIn/SignInResetPasswordForm";
import Checkbox, { CheckboxProps } from "@insite/mobius/Checkbox";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { IconProps } from "@insite/mobius/Icon/Icon";
import Eye from "@insite/mobius/Icons/Eye";
import EyeOff from "@insite/mobius/Icons/EyeOff";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

type Props = ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps> & HasHistory;

const mapStateToProps = (state: ApplicationState) => {
    const { search } = getLocation(state);
    const createAccountPageLink = getPageLinkByPageType(state, "CreateAccountPage");

    return {
        isSigningIn: state.context.isSigningIn,
        session: state.context.session,
        createAccountUrl: createAccountPageLink ? `${createAccountPageLink.url}${search}` : undefined,
        showRememberMe: getSettingsCollection(state).accountSettings.rememberMe,
        currentLocation: getLocation(state),
        signInUrl: getPageLinkByPageType(state, "SignInPage")?.url,
    };
};

const mapDispatchToProps = {
    signIn,
};

/**
 * @deprecated Use signInExistingAccountStyles instead.
 */

// TODO:: remove the current styles after we merge the DL
export interface SignInExistingAccountStyles {
    rememberMeCheckbox?: CheckboxProps;
    rememberMeCheckboxGroup?: CheckboxGroupComponentProps;
    userNameTextField?: TextFieldProps;
    signInGridContainer?: GridContainerProps;
    icon?: IconProps;
    passwordTextField?: TextFieldProps;
    signInButton?: ButtonPresentationProps;
    registerButton?: ButtonPresentationProps;
    signInButtonGridItem?: GridItemProps;
    forgotPasswordGridItem?: InjectableCss;
    rememberMeGridItem?: GridItemProps;
    passwordGridItem?: GridItemProps;
    userNameGridItem?: GridItemProps;
    forgotPasswordLink?: LinkPresentationProps;
    resetPasswordModal?: ModalPresentationProps;
}

export const signInExistingAccountStyles: SignInExistingAccountStyles = {
    signInGridContainer: {
        css: css`
            padding: 15px;
            width: 400px;
            min-width: 300px;
            ${screen("lte", "md")`width: auto;`}
        `,
    },
    icon: {
        css: css`
            padding-top: 50px;
        `,
    },
    userNameGridItem: {
        width: 12,
    },
    passwordGridItem: {
        width: 12,
        css: css`
            position: relative;
        `,
    },
    signInButtonGridItem: {
        width: 12,
        css: css`
            display: flex;
            justify-content: space-between;
            background: ${color("common.ui3")};
            padding: 0 15px;
            button {
                margin: 17px 0;
                min-width: 150px;
                ${screen("lte", "md")`min-width: auto;`}
            }
        `,
    },
    rememberMeGridItem: {
        width: 12,
        css: css`
            align-items: center;
            justify-content: flex-end;
            padding: 0 15px 15px;
        `,
    },
    forgotPasswordGridItem: {
        css: css`
            position: absolute;
            right: 20px;
            top: 10px;
        `,
    },
    forgotPasswordLink: {
        color: "primary",
        typographyProps: {
            size: 13,
        },
    },
    registerButton: {
        variant: "secondary",
    },
    resetPasswordModal: {
        sizeVariant: "small",
    },
};

export const signInExistingAccount = signInExistingAccountStyles;
const styles = signInExistingAccountStyles;

const XngSignIn: FC<Props> = (props: Props) => {
    const [rememberMe, setRememberMe] = React.useState(false);
    const [showPassword, setShowPassword] = React.useState(false);
    const [userName, setUserName] = React.useState("");
    const [password, setPassword] = React.useState("");
    const [isSignInClicked, setIsSignInClicked] = React.useState(false);
    const [isResetPasswordModalOpen, setIsResetPasswordModalOpen] = React.useState(false);
    const [errorMessage, setErrorMessage] = React.useState("");
    const [isLoading, setIsLoading] = React.useState(false);

    const signInHandler = () => {
        const generatedReturnUrl = props.currentLocation.pathname + props.currentLocation.search;

        setIsSignInClicked(true);

        if (!userName || !password) {
            return;
        }

        const queryParams = parseQueryString<{ returnUrl?: string }>(window.location.search);
        const returnUrl = queryParams.returnUrl?.toString();

        setErrorMessage("");
        setIsLoading(true);
        props.signIn({
            userName,
            password,
            rememberMe,
            returnUrl: returnUrl || generatedReturnUrl,
            onError: error => {
                error = error || translate("Invalid Username/Password combination");
                setErrorMessage(error);
                setIsLoading(false);
            },
        });
    };

    const onKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "Enter") {
            signInHandler();
        }
    };

    const onResetPasswordClose = () => {
        setIsResetPasswordModalOpen(false);
    };

    const handleCreateNewAccount = () => {
        const { history, createAccountUrl } = props;

        if (!createAccountUrl) {
            return;
        }

        history.push(createAccountUrl);
    };

    const userErrorMessage = !userName && isSignInClicked ? siteMessage("SignInInfo_UserName_Required") : "";
    const passwordErrorMessage =
        !password && isSignInClicked ? siteMessage("SignInInfo_Password_Required") : errorMessage || "";

    return (
        <StyledWrapper
            css={css`
                position: relative;
                width: 100%;
            `}
        >
            <GridContainer {...styles.signInGridContainer}>
                <GridItem {...styles.userNameGridItem}>
                    <TextField
                        id="userName"
                        {...styles.userNameTextField}
                        label={translate("User Name")}
                        onChange={e => setUserName(e.currentTarget.value)}
                        value={userName}
                        error={userErrorMessage}
                        autoComplete="username"
                        placeholder={translate("Type your email address")}
                        data-test-selector="signIn_userName"
                        maxLength={256}
                    />
                </GridItem>
                <GridItem {...styles.passwordGridItem}>
                    <StyledWrapper {...styles.forgotPasswordGridItem}>
                        <Link
                            {...styles.forgotPasswordLink}
                            onClick={() => setIsResetPasswordModalOpen(true)}
                            tabIndex={-1}
                        >
                            {translate("Forgot Password?")}
                        </Link>
                        <Modal
                            headline={translate("Reset Password")}
                            sizeVariant="small"
                            isOpen={isResetPasswordModalOpen}
                            handleClose={() => onResetPasswordClose()}
                        >
                            <SignInResetPasswordForm onClose={() => onResetPasswordClose()} />
                        </Modal>
                    </StyledWrapper>
                    <TextField
                        id="password"
                        {...styles.passwordTextField}
                        label={translate("Password")}
                        onChange={e => setPassword(e.currentTarget.value)}
                        onKeyPress={onKeyPress}
                        value={password}
                        error={passwordErrorMessage}
                        iconProps={{ ...styles.icon, src: showPassword ? EyeOff : Eye }}
                        iconClickableProps={{
                            onClick: () => {
                                setShowPassword(!showPassword);
                            },
                        }}
                        placeholder={translate("Type your password")}
                        autoComplete="current-password"
                        data-test-selector="signIn_password"
                        type={showPassword ? "text" : "password"}
                        maxLength={256}
                        clickableText={password}
                    />
                </GridItem>
                {props.showRememberMe && (
                    <GridItem {...styles.rememberMeGridItem}>
                        <CheckboxGroup {...styles.rememberMeCheckboxGroup}>
                            <Checkbox
                                uid="rememberMe"
                                {...styles.rememberMeCheckbox}
                                checked={rememberMe}
                                onChange={(e, value) => setRememberMe(value)}
                            >
                                {translate("Remember Me")}
                            </Checkbox>
                        </CheckboxGroup>
                    </GridItem>
                )}
                <GridItem {...styles.signInButtonGridItem}>
                    <Button
                        {...styles.registerButton}
                        onClick={handleCreateNewAccount}
                        data-test-selector="register_submit"
                    >
                        {translate("Register")}
                    </Button>
                    <Button {...styles.signInButton} onClick={signInHandler} data-test-selector="signIn_submit">
                        {translate("Sign In")}
                    </Button>
                </GridItem>
            </GridContainer>
            {isLoading && <Spinner />}
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withHistory(XngSignIn));
